import {
    AutomationCondition,
    AutomationConditionObject,
    AutomationRule,
    RuleSearchType
} from '../../rules.types';
import { isDefined, isNumber, isString } from 'lib/typeguards';
import {
    AUTOMATIONS,
    CUSTOMERS_VIEW,
    LOCATION_VIEW,
    MARKETING_AUDIENCES_VIEW,
    MARKETING_VOUCHERS_VIEW
} from 'config/routes';
import { RuleMethod } from '../automations.types';
import { extendedPredicates, functionRegex } from '@pepperhq/types-sdk';

// Generic helpers

const labelFrom =
    (map: Record<string, string>) =>
    (key: string): string =>
        map[key] ?? key;

const isOneOf =
    (keys: readonly string[]) =>
    (val: any): boolean =>
        keys.includes(val);

// Object helpers

export const conditionObjectItemToLabel = (
    object: string | number | { v: string | number; title?: string },
    includeId = false
) => {
    if (isString(object) || isNumber(object)) {
        return object;
    }
    if (isDefined(object.title)) {
        if (includeId) {
            return `${object.title} [${object.v}]`;
        }
        return object.title;
    }
    return object.v;
};

export const conditionObjectToLabel = (object: AutomationConditionObject) => {
    if (Array.isArray(object)) {
        return `"${object.map(item => conditionObjectItemToLabel(item)).join(', ')}"`;
    }
    return `"${conditionObjectItemToLabel(object)}"`;
};

export const conditionObjectToError = (
    object: (string | { v: string; error?: string }) | (string | { v: string; error?: string })[]
) => {
    if (Array.isArray(object)) {
        return object
            .filter(item => (isString(item) ? false : !!item.error))
            .map(item => (isString(item) ? item : `${item.error}: ${item.v}`));
    }
    return isString(object) ? false : object.error;
};

// Subject label maps

const stringSubjectLabels: Record<string, string> = {
    type: 'the action type',
    'metadata.audienceId': 'the audience',
    'context.location._id': 'the location',
    'context.clientPlatform': 'the platform',
    'when.daypart': 'the day part',
    'context.user.primaryPlatform': 'the primary platform',
    'metadata.scenario': 'the scenario',
    'metadata.order.scenario': 'the order scenario',
    'metadata.channel': 'the channel',
    'metadata.voucherId': 'the voucher',
    'metadata.voucherSchemeId': 'the voucher scheme',
    'metadata.voucherTitle': 'the voucher title',
    'metadata.username': 'the username',
    'metadata.credential.id': 'the credential',
    'metadata.awardId': 'the award',
    'metadata.perkId': 'the perk',
    'metadata.trigger': 'the trigger',
    'metadata.schemeId': 'the scheme',
    'metadata.status': 'the status',
    'metadata.externalId': 'the external ID',
    'metadata.type': 'the metadata type',
    'metadata.ratePlan': 'the rate plan',
    'metadata.bookingSource': 'the booking source',
    'metadata.booker.email': 'the booker email',
    'metadata.booker.firstName': 'the booker first name',
    'metadata.booker.lastName': 'the booker last name',
    'metadata.booker.phone': 'the booker phone'
};

const relativeDateSubjectLabels: Record<string, string> = {
    'when.dayofweek': 'the day of week',
    'when.weekofyear': 'the week of year',
    'when.monthofyear': 'the month of year'
};

const numberSubjectLabels: Record<string, string> = {
    'metadata.value.amount': 'the order amount',
    'metadata.pointsAdded': 'the points added',
    'metadata.pointsAvailable': 'the points available',
    'metadata.pointsRefunded': 'the points refunded',
    'metadata.pointsRedeemed': 'the points redeemed',
    'metadata.lengthOfStayNights': 'the length of stay (nights)',
    'metadata.cost': 'the cost',
    'metadata.taxAdded': 'the tax added',
    'metadata.taxToAdd': 'the tax to add',
    'metadata.guests': 'the number of guests'
};

const dateStringSubjectLabels: Record<string, string> = {
    'metadata.arrival': 'the arrival date',
    'metadata.departure': 'the departure date'
};

const booleanSubjectLabels: Record<string, string> = {
    'metadata.hasAgreedToReceiveMarketing': 'the user has agreed to marketing',
    'metadata.hasAgreedToShareData': 'the user has agreed to share data'
};

const basketSubjectLabels: Record<string, string> = {
    'metadata.basket': 'the basket has',
    'metadata.order.items': 'the order items have'
};

const paymentSubjectLabels: Record<string, string> = {
    'metadata.order.payments': 'order payments'
};

const extendedSubjectLabels: Record<string, string> = {
    'context.user._id': 'the user'
};

// Predicate label maps

const stringPredicateLabels: Record<string, string> = {
    EQ: 'is',
    EQUAL: 'is',
    NEQ: 'is not',
    NOT_EQUAL: 'is not',
    IN: 'is in',
    NIN: 'is not in',
    NOT_IN: 'is not in',
    ENDS_WITH: 'ends with'
};

const numberPredicateLabels: Record<string, string> = {
    EQ: 'is',
    EQUAL: 'is',
    NEQ: 'is not',
    NOT_EQUAL: 'is not',
    GT: 'is greater than',
    GREATER: 'is greater than',
    LT: 'is less than',
    LESSER: 'is less than',
    GTE: 'is greater than or equal to',
    GREATER_THAN_OR_EQUAL: 'is greater than or equal to',
    LTE: 'is less than or equal to',
    LESS_THAN_OR_EQUAL: 'is less than or equal to',
    IN: 'is in',
    NIN: 'is not in',
    NOT_IN: 'is not in'
};

const booleanPredicateLabels: Record<string, string> = {
    EQ: 'is',
    EQUAL: 'is',
    NEQ: 'is not',
    NOT_EQUAL: 'is not'
};

const basketPredicateLabels: Record<string, string> = {
    HAS_PRODUCT_ID: 'a product with ID:',
    HAS_CATEGORY_ID: 'a category with ID:'
};

const paymentPredicateLabels: Record<string, string> = {
    HAS_PAYMENT_WITH_TYPE: 'have a payment with type:'
};

const extendedPredicateLabels: Record<string, string> = {
    IS_MEMBER_OF: 'is a member of',
    NOT_IS_MEMBER_OF: 'is not a member of'
};

// Derived label functions

const stringSubjectToLabel = labelFrom(stringSubjectLabels);
const relativeDateSubjectToLabel = labelFrom(relativeDateSubjectLabels);
const numberSubjectToLabel = labelFrom(numberSubjectLabels);
const dateStringSubjectToLabel = labelFrom(dateStringSubjectLabels);
const booleanSubjectToLabel = labelFrom(booleanSubjectLabels);
const basketSubjectToLabel = labelFrom(basketSubjectLabels);
const paymentSubjectToLabel = labelFrom(paymentSubjectLabels);
const extendedSubjectToLabel = labelFrom(extendedSubjectLabels);

const stringPredicateToLabel = labelFrom(stringPredicateLabels);
const numberPredicateToLabel = labelFrom(numberPredicateLabels);
const booleanPredicateToLabel = labelFrom(booleanPredicateLabels);
const basketPredicateToLabel = labelFrom(basketPredicateLabels);
const paymentPredicateToLabel = labelFrom(paymentPredicateLabels);
const extendedPredicateToLabel = labelFrom(extendedPredicateLabels);

// Derived type guards

const stringSubjectKeys = Object.keys(stringSubjectLabels);
const relativeDateSubjectKeys = Object.keys(relativeDateSubjectLabels);
const numberSubjectKeys = Object.keys(numberSubjectLabels);
const dateStringSubjectKeys = Object.keys(dateStringSubjectLabels);
const booleanSubjectKeys = Object.keys(booleanSubjectLabels);
const basketSubjectKeys = Object.keys(basketSubjectLabels);
const paymentSubjectKeys = Object.keys(paymentSubjectLabels);
const extendedSubjectKeys = Object.keys(extendedSubjectLabels);

const stringPredicateKeys = Object.keys(stringPredicateLabels);
const numberPredicateKeys = Object.keys(numberPredicateLabels);
const dateStringPredicateKeys = numberPredicateKeys;
const booleanPredicateKeys = Object.keys(booleanPredicateLabels);
const basketPredicateKeys = Object.keys(basketPredicateLabels);
const paymentPredicateKeys = Object.keys(paymentPredicateLabels);

const isStringSubject = isOneOf(stringSubjectKeys);
const isStringPredicate = isOneOf(stringPredicateKeys);
const isRelativeDateSubject = isOneOf(relativeDateSubjectKeys);
const isNumberSubject = isOneOf(numberSubjectKeys);
const isNumberPredicate = isOneOf(numberPredicateKeys);
const isDateStringSubject = isOneOf(dateStringSubjectKeys);
const isDateStringPredicate = isOneOf(dateStringPredicateKeys);
const isBooleanSubject = isOneOf(booleanSubjectKeys);
const isBooleanPredicate = isOneOf(booleanPredicateKeys);
const isBasketSubject = isOneOf(basketSubjectKeys);
const isBasketPredicate = isOneOf(basketPredicateKeys);
const isPaymentSubject = isOneOf(paymentSubjectKeys);
const isPaymentPredicate = isOneOf(paymentPredicateKeys);
const isExtendedSubject = isOneOf(extendedSubjectKeys);
const isExtendedPredicate = isOneOf(extendedPredicates);

const conditionLinkSubjectRouteMap: Record<string, string> = {
    'metadata.audienceId': MARKETING_AUDIENCES_VIEW.replace(':audienceId', '$id'),
    'context.location._id': LOCATION_VIEW.replace(':locationId', '$id'),
    'metadata.voucherId': MARKETING_VOUCHERS_VIEW.replace(':voucherId', '$id'),
    'context.user._id': CUSTOMERS_VIEW.replace(':customerId', '$id')
};

const conditionRulesLinkSubjectRouteMap: Record<string, string> = {
    'metadata.audienceId': RuleSearchType.Audience,
    'metadata.perkId': RuleSearchType.Perk
};

const effectMethodToResourceType: Record<string, string> = {
    addMemberToSegment: RuleSearchType.Audience,
    removeMemberFromSegment: RuleSearchType.Audience,
    grantPerkFromImpact: RuleSearchType.Perk,
    revokeFromImpact: RuleSearchType.Perk
};

const getResourceIdFromEffect = (effect: AutomationRule['effect']) => {
    if ('impact' in effect && effect.impact.parameters) {
        switch (effect.method) {
            case RuleMethod.AddMemberToSegment:
            case RuleMethod.RemoveMemberFromSegment:
                return 'segmentId' in effect.impact.parameters
                    ? effect.impact.parameters.segmentId
                    : undefined;
            case RuleMethod.GrantPerkFromImpact:
            case RuleMethod.RevokeFromImpact:
                return 'perkId' in effect.impact.parameters ? effect.impact.parameters.perkId : undefined;
            default:
                return undefined;
        }
    }
    return undefined;
};

const effectToRulesLink = (effect: AutomationRule['effect']) => {
    const searchType = effectMethodToResourceType[effect.method];
    if (!searchType) {
        return undefined;
    }
    const resourceId = getResourceIdFromEffect(effect);
    if (!isString(resourceId)) {
        return undefined;
    }
    const query = new URLSearchParams();
    query.append('type', searchType);
    query.append('search', resourceId);
    query.append('refresh', 'true');
    return `${AUTOMATIONS}?${query.toString()}`;
};

const buildConditionLink = (
    item: string | { v: string; title?: string },
    routeTemplate?: string,
    rulesLinkType?: string
) => {
    const id = isString(item) ? item : item.v;
    const rulesLink = rulesLinkType
        ? `${AUTOMATIONS}?type=${rulesLinkType}&search=${id}&refresh=true`
        : undefined;
    return {
        label: conditionObjectItemToLabel(item, true),
        link: routeTemplate ? routeTemplate.replace('$id', id) : undefined,
        rulesLink
    };
};

const conditionToLinks = (condition: AutomationCondition) => {
    const routeTemplate = conditionLinkSubjectRouteMap[condition.subject];
    const rulesLinkType = conditionRulesLinkSubjectRouteMap[condition.subject];
    if (!routeTemplate && !rulesLinkType) {
        return undefined;
    }
    const obj = condition.object;
    if (Array.isArray(obj)) {
        return (obj as (string | { v: string; title?: string })[]).map(item =>
            buildConditionLink(item, routeTemplate, rulesLinkType)
        );
    }
    return [buildConditionLink(obj, routeTemplate)];
};

function isFunctionExpression(subject: string): boolean {
    return functionRegex.test(subject);
}

function getFunctionExpressionLabel(condition: AutomationCondition): string | undefined {
    const match = functionRegex.exec(condition.subject);
    if (!match) {
        return undefined;
    }

    const [, funcName, innerSubject] = match;
    const func = funcName.toUpperCase();
    const predLabel = numberPredicateToLabel(condition.predicate);
    const obj = condition.object;

    switch (func) {
        case 'DAY':
            return `If the day of ${dateStringSubjectToLabel(innerSubject)} ${predLabel} ${obj}`;
        case 'MONTH':
            return `If the month of ${dateStringSubjectToLabel(innerSubject)} ${predLabel} ${obj}`;
        case 'YEAR':
            return `If the year of ${dateStringSubjectToLabel(innerSubject)} ${predLabel} ${obj}`;
        case 'WEEKDAY':
            return `If the weekday of ${dateStringSubjectToLabel(innerSubject)} ${predLabel} ${obj}`;
        case 'LEN':
            return `If the length of ${innerSubject} ${predLabel} ${obj}`;
        case 'ANY': {
            const strPred = stringPredicateToLabel(condition.predicate);
            const objLabel = conditionObjectToLabel(condition.object);
            return `If any ${innerSubject} ${strPred} ${objLabel}`;
        }
        case 'EVERY': {
            const strPred = stringPredicateToLabel(condition.predicate);
            const objLabel = conditionObjectToLabel(condition.object);
            return `If every ${innerSubject} ${strPred} ${objLabel}`;
        }
        default:
            return undefined;
    }
}

interface ConditionMatcher {
    isSubject: (s: any) => boolean;
    isPredicate: (p: any) => boolean;
    subjectToLabel: (s: string) => string;
    predicateToLabel: (p: string) => string;
}

const conditionMatchers: ConditionMatcher[] = [
    {
        isSubject: isStringSubject,
        isPredicate: isStringPredicate,
        subjectToLabel: stringSubjectToLabel,
        predicateToLabel: stringPredicateToLabel
    },
    {
        isSubject: isRelativeDateSubject,
        isPredicate: isNumberPredicate,
        subjectToLabel: relativeDateSubjectToLabel,
        predicateToLabel: numberPredicateToLabel
    },
    {
        isSubject: isNumberSubject,
        isPredicate: isNumberPredicate,
        subjectToLabel: numberSubjectToLabel,
        predicateToLabel: numberPredicateToLabel
    },
    {
        isSubject: isDateStringSubject,
        isPredicate: isDateStringPredicate,
        subjectToLabel: dateStringSubjectToLabel,
        predicateToLabel: numberPredicateToLabel
    },
    {
        isSubject: isBooleanSubject,
        isPredicate: isBooleanPredicate,
        subjectToLabel: booleanSubjectToLabel,
        predicateToLabel: booleanPredicateToLabel
    },
    {
        isSubject: isBasketSubject,
        isPredicate: isBasketPredicate,
        subjectToLabel: basketSubjectToLabel,
        predicateToLabel: basketPredicateToLabel
    },
    {
        isSubject: isPaymentSubject,
        isPredicate: isPaymentPredicate,
        subjectToLabel: paymentSubjectToLabel,
        predicateToLabel: paymentPredicateToLabel
    },
    {
        isSubject: isExtendedSubject,
        isPredicate: isExtendedPredicate,
        subjectToLabel: extendedSubjectToLabel,
        predicateToLabel: extendedPredicateToLabel
    }
];

export const getConditionData = (condition: AutomationCondition) => {
    const { subject, predicate, object } = condition;
    const objLabel = conditionObjectToLabel(object);

    let label: string | undefined;

    const matcher = conditionMatchers.find(m => m.isSubject(subject) && m.isPredicate(predicate));
    if (matcher) {
        const subLabel = matcher.subjectToLabel(subject);
        const predLabel = matcher.predicateToLabel(predicate);
        label = `If ${subLabel} ${predLabel} ${objLabel}`;
    } else if (isFunctionExpression(subject)) {
        label = getFunctionExpressionLabel(condition);
    }

    if (!isDefined(label)) {
        label = `If ${subject} ${predicate} ${objLabel}`;
    }

    return {
        label,
        error: conditionObjectToError(condition.object),
        condition,
        links: conditionToLinks(condition)
    };
};

export const getAudienceNameFromEffect = (effect: AutomationRule['effect']) => {
    if ('title' in effect) {
        return effect.title;
    }
    if ('impact' in effect && 'segmentId' in effect.impact.parameters) {
        return effect.impact.parameters.segmentId;
    }
    return 'Unknown audience';
};

export const getPerkNameFromEffect = (effect: AutomationRule['effect']) => {
    if ('title' in effect) {
        return effect.title;
    }
    if ('impact' in effect && 'perkId' in effect.impact.parameters) {
        return effect.impact.parameters.perkId;
    }
    return 'Unknown perk';
};

export const getEffectData = (effect: AutomationRule['effect']) => {
    let label: string = undefined;
    switch (effect.method) {
        case RuleMethod.AddMemberToSegment: {
            label = `The user will be added to "${getAudienceNameFromEffect(effect)}" audience`;
            break;
        }
        case RuleMethod.RemoveMemberFromSegment: {
            label = `The user will be removed from "${getAudienceNameFromEffect(effect)}" audience`;
            break;
        }
        case RuleMethod.GrantPerkFromImpact:
            label = `"${getPerkNameFromEffect(effect)}" perk will be granted to the user`;
            break;
        case RuleMethod.RevokeFromImpact:
            label = `"${getPerkNameFromEffect(effect)}" perk will be revoked from the user`;
            break;
        case RuleMethod.UnverifyManyFromImpact:
            label = 'Unverify many from impact';
            break;
        case RuleMethod.CreateScheduledTask:
            label = 'Create scheduled task';
            break;
        default:
            label = 'Unknown';
    }
    return {
        label,
        effect,
        layer: 1,
        errors: 'errors' in effect ? effect.errors : undefined,
        rulesLink: effectToRulesLink(effect)
    };
};
